const TRAIL_LIFETIME_MS = 360;
const MAX_POINTS = 14;
const INTERPOLATION = 0.18;
const BASE_WIDTH = 1.2;
const INTERACTIVE_WIDTH = 2.1;
const STROKE_RGB = '190, 130, 140';
const GLOW_RGB = '255, 244, 244';
const INTERACTIVE_SELECTOR = [
  'a[href]',
  'button',
  '[role="button"]',
  'summary',
  'input',
  'select',
  'textarea',
  '.button',
  '.card',
  '[data-cursor-ribbon-interactive]',
].join(', ');

export function shouldEnableCursorRibbon({
  matchMedia = globalThis.matchMedia?.bind(globalThis),
} = {}) {
  if (typeof matchMedia !== 'function') return false;

  return (
    matchMedia('(hover: hover)').matches &&
    matchMedia('(pointer: fine)').matches &&
    !matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function pruneTrailPoints(points, now, lifetime = TRAIL_LIFETIME_MS) {
  return points.filter((point) => now - point.time <= lifetime);
}

function getViewportSize() {
  return {
    width: globalThis.innerWidth || document.documentElement.clientWidth || 0,
    height: globalThis.innerHeight || document.documentElement.clientHeight || 0,
  };
}

function isInteractiveTarget(target) {
  return target instanceof Element && Boolean(target.closest(INTERACTIVE_SELECTOR));
}

function drawRibbon(ctx, points, isInteractive) {
  if (points.length < 2) return;

  const newestPoint = points[points.length - 1];

  for (let index = 1; index < points.length; index += 1) {
    const previousPoint = points[index - 1];
    const currentPoint = points[index];
    const progress = index / (points.length - 1);
    const ageFade = 1 - Math.min(1, (newestPoint.time - currentPoint.time) / TRAIL_LIFETIME_MS);
    const alpha = progress * ageFade * (isInteractive ? 0.32 : 0.22);

    ctx.beginPath();
    ctx.moveTo(previousPoint.x, previousPoint.y);
    ctx.lineTo(currentPoint.x, currentPoint.y);
    ctx.strokeStyle = `rgba(${STROKE_RGB}, ${alpha})`;
    ctx.lineWidth = isInteractive
      ? BASE_WIDTH + (INTERACTIVE_WIDTH - BASE_WIDTH) * progress
      : BASE_WIDTH * (0.7 + progress * 0.55);
    ctx.stroke();
  }

  ctx.beginPath();
  ctx.arc(newestPoint.x, newestPoint.y, isInteractive ? 1.9 : 1.2, 0, Math.PI * 2);
  ctx.fillStyle = `rgba(${GLOW_RGB}, ${isInteractive ? 0.25 : 0.16})`;
  ctx.fill();
}

export function initCursorRibbon() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (!shouldEnableCursorRibbon()) return;

  const canvas = document.querySelector('[data-cursor-ribbon]');
  if (!(canvas instanceof HTMLCanvasElement)) return;

  const context = canvas.getContext('2d');
  if (!context) return;

  const state = {
    active: false,
    frameId: 0,
    interactive: false,
    points: [],
    pointer: { x: 0, y: 0 },
    position: { x: 0, y: 0 },
  };

  document.documentElement.classList.add('has-cursor-ribbon');

  context.lineCap = 'round';
  context.lineJoin = 'round';

  const resizeCanvas = () => {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const viewport = getViewportSize();

    canvas.width = Math.max(1, Math.round(viewport.width * dpr));
    canvas.height = Math.max(1, Math.round(viewport.height * dpr));
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
  };

  const stop = () => {
    window.cancelAnimationFrame(state.frameId);
    state.frameId = 0;
  };

  const clearTrail = () => {
    state.points = [];
    context.clearRect(0, 0, canvas.width, canvas.height);
  };

  const render = () => {
    const now = performance.now();
    state.position.x += (state.pointer.x - state.position.x) * INTERPOLATION;
    state.position.y += (state.pointer.y - state.position.y) * INTERPOLATION;

    if (state.active) {
      state.points.push({
        x: state.position.x,
        y: state.position.y,
        time: now,
      });
    }

    state.points = pruneTrailPoints(state.points, now).slice(-MAX_POINTS);

    context.clearRect(0, 0, canvas.width, canvas.height);
    drawRibbon(context, state.points, state.interactive);

    if (state.active || state.points.length > 1) {
      state.frameId = window.requestAnimationFrame(render);
      return;
    }

    stop();
  };

  const start = () => {
    if (state.frameId) return;
    state.frameId = window.requestAnimationFrame(render);
  };

  const handlePointerMove = (event) => {
    state.active = true;
    state.pointer.x = event.clientX;
    state.pointer.y = event.clientY;

    if (!state.points.length) {
      state.position.x = event.clientX;
      state.position.y = event.clientY;
    }

    state.interactive = isInteractiveTarget(event.target);
    start();
  };

  const handlePointerLeave = () => {
    state.active = false;
  };

  const handleVisibilityChange = () => {
    if (!document.hidden) {
      start();
      return;
    }

    state.active = false;
    stop();
    clearTrail();
  };

  resizeCanvas();
  window.addEventListener('resize', resizeCanvas, { passive: true });
  document.addEventListener('pointermove', handlePointerMove, { passive: true });
  document.addEventListener('pointerleave', handlePointerLeave, { passive: true });
  document.addEventListener('visibilitychange', handleVisibilityChange);
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  initCursorRibbon();
}
